<template>
  <span class="pay-plan-lifecycle-badge" :class="`pay-plan-lifecycle-badge--${variantKey}`">
    <span v-if="variantKey === 'terminated' && !label" aria-hidden="true">&#x2715;</span>
    {{ displayLabel }}
  </span>
</template>

<script setup>
import { computed } from "vue";
import {
  normalizePayPlanLifecycleStatus,
  PAY_PLAN_LIFECYCLE_STATUS,
} from "@components/PayPlanRowCard/payPlanRowCardUtils";

const props = defineProps({
  status: {
    type: String,
    required: true,
  },
  /** When status is Proposed and approval is Rejected, shows the red Rejected badge (R5). */
  approvalStatus: {
    type: String,
    default: "",
  },
  /** Replaces the default badge text while keeping the status colours. */
  label: {
    type: String,
    default: "",
  },
});

const lifecycle = computed(() => normalizePayPlanLifecycleStatus(props.status));

const variantKey = computed(() => {
  if (
    lifecycle.value === PAY_PLAN_LIFECYCLE_STATUS.PROPOSED &&
    props.approvalStatus === "Rejected"
  ) {
    return "rejected";
  }
  return lifecycle.value;
});

const displayLabel = computed(() => {
  if (props.label) return props.label;
  switch (variantKey.value) {
    case "draft":
      return "Draft";
    case "relieved":
      return "Relieved";
    case "proposed":
      return "Proposed";
    case "rejected":
      return "Rejected";
    case "in-force":
      return "In Force";
    case "terminated":
      return "Terminated";
    case "voided":
      return "Voided";
    default:
      return "";
  }
});
</script>

<style scoped>
.pay-plan-lifecycle-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
  padding: 2px 8px;
  border-radius: 9999px;
  font-family: Inter, sans-serif;
  font-size: 12px;
  font-weight: 500;
  line-height: 18px;
  white-space: nowrap;
}

.pay-plan-lifecycle-badge--proposed {
  border: 1px dashed var(--component-colors-utility-fuchsia-utility-fuchsia-500, #d444f1);
  background: var(--component-colors-utility-fuchsia-utility-fuchsia-50, #fdf4ff);
  color: var(--component-colors-utility-fuchsia-utility-fuchsia-700, #9f1ab1);
}

.pay-plan-lifecycle-badge--rejected {
  border: 1px solid #fecdca;
  background: #fef3f2;
  color: #b42318;
}

.pay-plan-lifecycle-badge--in-force {
  border: 1.5px solid #079455;
  color: #067647;
}

.pay-plan-lifecycle-badge--draft {
  border: 1px dashed var(--component-colors-utility-fuchsia-utility-fuchsia-500, #d444f1);
  background: var(--component-colors-utility-fuchsia-utility-fuchsia-50, #fdf4ff);
  color: var(--component-colors-utility-fuchsia-utility-fuchsia-700, #9f1ab1);
}

.pay-plan-lifecycle-badge--relieved {
  border: 1px solid #d0d5dd;
  background: #f2f4f7;
  color: #344054;
}

.pay-plan-lifecycle-badge--terminated {
  border: 1.5px solid #d92d20;
  background: #fef3f2;
  color: #b42318;
}

.pay-plan-lifecycle-badge--voided {
  border: 1.5px solid #d92d20;
  color: #b42318;
}
</style>
